import Checkbox from 'src/components/Checkbox';
import FontAwesomeIcon from 'src/components/Icon';
import GroupedHeaderControls from 'src/pages/ServiceRequests/GroupedHeaderControls';
import { convertTime } from 'src/helpers/time';
import Label from 'src/components/Label';
import { sortByDate } from 'src/components/Table/sorting';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { ICONS, PATHS } from 'src/common/constants';
import IcoMoon from 'src/components/IcoMoon';
import Status from 'src/components/Status';
import { CSSProperties, ReactElement } from 'react';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';

const isValidDate = (date: any) => date !== null && moment(date).year() > 2020;

export interface TableColumn {
  label: string | ReactElement;
  accessor?: string;
  sortable?: boolean;
  style?: CSSProperties;
  minWidth?: string;
  xsHide?: boolean;
  showOverflow?: boolean;
  textOverflow?: boolean;
  groupedHeaderCell?: (data: any) => ReactElement;
  groupedHeaderCellSpan?: number;
  xsGroupedHeaderCellSpan?: number;
  cell?: (data: any) => ReactElement | string;
  sortingAlgorithm?: (a: any, b: any) => number;
}

export interface CreateTableColumnsParams {
  allSelected: boolean;
  onSelectAll: () => void;
  expandedGroups: any[];
  setExpandedGroups: (groups: any[]) => void;
  selectedServiceRequests: string[];
  onSelectGroup: (ids: any) => void;
  onDeleteGroup: (id: any) => void;
  onSelectServiceRequest: (id: any) => void;
  onRemoveFromGroup: (itemId: any, groupId: any) => void;
  branding: any;
  hasTarionNumbers: boolean;
}

export const createServiceRequestTableColumns = ({
  allSelected,
  onSelectAll,
  expandedGroups,
  setExpandedGroups,
  selectedServiceRequests,
  onSelectGroup,
  onDeleteGroup,
  onSelectServiceRequest,
  onRemoveFromGroup,
  branding,
  hasTarionNumbers,
}: CreateTableColumnsParams): TableColumn[] => {
  const baseColumns = [
    {
      label: <Checkbox readOnly value={allSelected} onChange={onSelectAll} />,
      width: '8rem',
      sticky: true,
      groupedHeaderCell: (data: any) => (
        <GroupedHeaderControls
          data={data}
          onExpandGroup={() => setExpandedGroups([...expandedGroups, data.id])}
          onContractGroup={() =>
            setExpandedGroups(
              expandedGroups.filter((id: any) => id !== data.id)
            )
          }
          isSelected={
            allSelected ||
            data.children.every((c) => selectedServiceRequests.includes(c.id))
          }
          onSelectGroup={() => onSelectGroup(data.children.map((c) => c.id))}
          onDeleteGroup={() => onDeleteGroup(data.id)}
        />
      ),
      cell: (data: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {data.isChild && (
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => onRemoveFromGroup(data.id, data.groupId)}
            >
              <FontAwesomeIcon
                glyph="object-ungroup"
                weight="regular"
                size="big"
              />
            </div>
          )}
          <Checkbox
            checked={selectedServiceRequests.includes(data.id) || allSelected}
            name={data.id}
            value={selectedServiceRequests.includes(data.id) || allSelected}
            onChange={() => onSelectServiceRequest(data.id)}
            readOnly
          />
        </div>
      ),
    },
    {
      label: `${branding?.text?.suite} | SL#`,
      accessor: 'name',
      sortable: true,
      sticky: true,
      left: '8rem',
      groupedHeaderCell: (data) => {
        return (
          <span
            onClick={() => {
              setExpandedGroups(
                expandedGroups.includes(data.id)
                  ? expandedGroups.filter((id: any) => id !== data.id)
                  : [...expandedGroups, data.id]
              );
            }}
            style={{
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {data.name}
          </span>
        );
      },
      minWidth: '13rem',
      width: '0.25fr',
      groupedHeaderCellSpan: hasTarionNumbers ? 10 : 9,
      xsGroupedHeaderCellSpan: hasTarionNumbers ? 10 : 9,
      cell: (data) => (
        <Link
          to={{
            pathname: PATHS.SERVICE_REQUESTS_VIEW.replace(':id', data.id),
            state: { navigateTo: PATHS.SERVICE_REQUESTS_LIST },
          }}
        >
          {`${data.name}` + (data.stratalot ? ` | ${data.stratalot}` : '')}
        </Link>
      ),
    },
    {
      label: 'Description',
      accessor: 'description',
      textOverflow: true,
      sortable: true,
      xsHide: false,
      minWidth: '40rem',
      cell: (data: any) => {
        const description: string = data.description?.trim() ?? '';
        return (
          <ExpandableText
            text={description}
            characterLimit={80}
          />
        );
      },
    },
    {
      label: 'Area',
      accessor: 'area',
      xsHide: false,
      width: '12rem',
      sortable: true,
    },
    {
      label: 'Asset',
      xsHide: false,
      accessor: 'item',

      width: '15rem',
      sortable: true,
    },
    {
      label: 'Type',
      xsHide: false,
      accessor: 'type',
      width: '11rem',
      sortable: true,
    },
    {
      label: 'Status',
      showOverflow: true,
      accessor: 'status',
      width: '15rem',
      xsHide: false,
      sortable: true,
      cell: (data: any) => (
        <Status
          icon={<IcoMoon glyph={ICONS[data.status]} />}
          label={data.status}
        />
      ),
    },
    {
      label: 'Submitted On',
      sortable: true,
      accessor: 'createdDate',
      sortingAlgorithm: sortByDate,
      cell: (data: any) => {
        const days: number = moment().diff(moment(data.createdDate), 'days');
        const color: 'teal' | 'yellow' | 'red' =
          days <= 30 ? 'teal' : days <= 60 ? 'yellow' : 'red';
        return (
          <Label color={color} label={convertTime(data.createdDate, 'date')} />
        );
      },

      width: '13rem',
    },
    {
      label: 'Assigned To',
      accessor: 'serviceProvider',
      width: '18rem',
      sortable: true,
    },
    {
      label: 'Scheduled',
      accessor: 'scheduledAt',
      sortable: true,
      xsHide: false,
      sortingAlgorithm: sortByDate,
      cell: (data: any) =>
        isValidDate(data.scheduledAt)
          ? convertTime(data.scheduledAt, 'date')
          : '',
      width: '10rem',
    },
  ];

  // If no tarion numbers, just return the base columns.
  if (!hasTarionNumbers) return baseColumns;

  // Add tarion column after Type column (position 6).
  const tarionColumn = {
    label: 'Tarion #',
    accessor: 'tarionItemNumber',
    sortable: true,
    xsHide: false,
    style: { width: '8rem' },
    minWidth: '8rem',
    cell: (data: any) => data.tarionItemNumber || '',
  };

  return [
    ...baseColumns.slice(0, 6), // First 6 columns.
    tarionColumn, // New tarion column when it is needed.
    ...baseColumns.slice(6), // Remaining columns.
  ];
};
